
import React, { createContext, useState, useEffect } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { AuthContextType, UserType } from './types';
import { persistUserSession } from './authUtils';
import * as authActions from './authActions';

// Create context
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// The AuthProvider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userType, setUserType] = useState<UserType>(null);
  const [loading, setLoading] = useState(true);

  // Restore user session from localStorage on component mount
  useEffect(() => {
    const storedUserType = localStorage.getItem('userType') as UserType;
    
    if (storedUserType) {
      setUserType(storedUserType);
    }
  }, []);
  
  // Store user type in local storage when it changes
  useEffect(() => {
    if (currentUser && userType) {
      persistUserSession(currentUser, userType);
    }
  }, [currentUser, userType]);

  // Create wrapped versions of the auth actions that include setUserType
  const wrappedLoginMember = (emailOrAhavexId: string, password: string) => {
    return authActions.loginMember(emailOrAhavexId, password, setUserType);
  };

  const wrappedLoginCustomer = (email: string, password: string) => {
    return authActions.loginCustomer(email, password, setUserType);
  };

  const wrappedLoginCustomerWithGoogle = () => {
    return authActions.loginCustomerWithGoogle(setUserType);
  };

  const wrappedRegisterMember = (
    email: string, 
    password: string, 
    profileData?: { fullName: string; ahavexId: string; address: string }
  ) => {
    if (profileData) {
      return authActions.registerMember(email, password, profileData, setUserType);
    }
    throw new Error('Profile data is required for Ahavexer registration');
  };

  const wrappedRegisterCustomer = (email: string, password: string) => {
    return authActions.registerCustomer(email, password, setUserType);
  };

  const wrappedLogout = async () => {
    await authActions.logout();
    setUserType(null);
  };

  // Subscribe to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      
      // If a user is found but no userType is set, try to restore it from localStorage
      if (user && !userType) {
        const storedUserType = localStorage.getItem('userType') as UserType;
        if (storedUserType) {
          setUserType(storedUserType);
        }
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, [userType]);

  const value = {
    currentUser,
    userType,
    loading,
    loginMember: wrappedLoginMember,
    loginCustomer: wrappedLoginCustomer,
    loginCustomerWithGoogle: wrappedLoginCustomerWithGoogle,
    registerMember: wrappedRegisterMember,
    registerCustomer: wrappedRegisterCustomer,
    logout: wrappedLogout,
    setUserType
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
